/**
 * Bitrix24 Disk operations — extracted from deal-context for reuse.
 *
 * All functions use the BX24 SDK loaded in the iframe.
 * Types are re-exported from deal-context/types for convenience.
 */
import type { BX24Result, DiskUploadedFile, DiskFolderChild, DiskAttachedObject } from '../deal-context/types';
// TODO: Убрать отладочные логи
import { dbg } from '../reference-data';

function callMethod<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  return new Promise((resolve, reject) => {
    window.BX24.callMethod(method, params, (result: BX24Result<T>) => {
      const err = result.error();
      if (err) {
        reject(new Error(`BX24 ${method}: ${err.description} (${err.code})`));
      } else {
        resolve(result.data());
      }
    });
  });
}

// ─── Folder operations ────────────────────────────────────────────────────────

/**
 * List direct children (files + subfolders) of a Bitrix24 Disk folder.
 */
export function getDiskFolderChildren(folderId: number): Promise<DiskFolderChild[]> {
  return callMethod<DiskFolderChild[]>('disk.folder.getchildren', { id: folderId });
}

/**
 * Given a parent folder ID, find the subfolder whose name starts with the given prefix
 * (e.g. "3." to find "3. Расчет"). Returns null if not found.
 * Uses absence of DOWNLOAD_URL to identify folders (files always have it).
 */
export async function findSubfolderByPrefix(
  parentFolderId: number,
  namePrefix: string,
): Promise<number | null> {
  const children = await getDiskFolderChildren(parentFolderId);
  const match = children.find(
    (c) => c.DOWNLOAD_URL === undefined && c.NAME.startsWith(namePrefix),
  );
  return match?.ID ?? null;
}

// ─── File upload ──────────────────────────────────────────────────────────────

/**
 * Upload a new file to a Bitrix24 Disk folder.
 */
export function uploadFileToDiskFolder(
  folderId: number,
  name: string,
  base64: string,
): Promise<DiskUploadedFile> {
  return callMethod<DiskUploadedFile>('disk.folder.uploadfile', {
    id: folderId,
    data: { NAME: name },
    fileContent: [name, base64],
    generateUniqueName: false,
  });
}

/**
 * Upload a new version of an existing Bitrix24 Disk file.
 * The file ID stays the same; only its content changes.
 */
export function uploadFileVersion(
  fileId: number,
  name: string,
  base64: string,
): Promise<DiskUploadedFile> {
  return callMethod<DiskUploadedFile>('disk.file.uploadversion', {
    id: fileId,
    data: { NAME: name },
    fileContent: [name, base64],
  });
}

/**
 * Upload-or-update a file in a folder by name.
 * If a file with the same name already exists, uploads a new version (preserves file ID).
 * Otherwise, creates a new file.
 */
export async function upsertFileToDiskFolder(
  folderId: number,
  name: string,
  base64: string,
): Promise<DiskUploadedFile> {
  const children = await getDiskFolderChildren(folderId);
  const existing = children.find(
    (c) => c.DOWNLOAD_URL !== undefined && c.NAME === name,
  );
  if (existing) {
    return uploadFileVersion(existing.ID, name, base64);
  }
  return uploadFileToDiskFolder(folderId, name, base64);
}

// ─── File metadata & download ────────────────────────────────────────────────

/**
 * Get metadata for a Bitrix Disk file by its numeric ID.
 * Returns DOWNLOAD_URL among other fields.
 */
export function getDiskFile(id: number): Promise<DiskUploadedFile> {
  return callMethod<DiskUploadedFile>('disk.file.get', { id });
}

/**
 * Get a disk attached object by its ID.
 * "Файл" UF fields on CRM entities store file references as attached object IDs.
 * The returned DOWNLOAD_URL (uf.php?...) includes an auth token and can be fetched cross-origin.
 */
export function getAttachedObject(id: number): Promise<DiskAttachedObject> {
  return callMethod<DiskAttachedObject>('disk.attachedObject.get', { id });
}

/**
 * Fetch the file bytes for a Bitrix file by its download URL.
 *
 * Handles:
 *  - Relative URLs → resolved against portal domain (BX24.getAuth().domain)
 *  - show_file.php URLs → routed through /bx24-proxy to avoid CORS
 *    (the endpoint has no Access-Control-Allow-Origin header;
 *     our own server proxies the request transparently)
 *  - Other absolute URLs (disk_file DOWNLOAD_URL with embedded auth token) → fetched directly
 */
export async function fetchDiskFile(downloadUrl: string): Promise<ArrayBuffer> {
  let url = downloadUrl;

  // TODO: Убрать отладочные логи — начало
  dbg(`fetchDiskFile: исходный URL = ${url.substring(0, 120)}...`);
  // TODO: Убрать отладочные логи — конец

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    const { domain } = window.BX24.getAuth();
    url = `https://${domain}${url.startsWith('/') ? '' : '/'}${url}`;
    // TODO: Убрать отладочные логи — начало
    dbg(`fetchDiskFile: относительный URL → дополнен доменом = ${url.substring(0, 120)}...`);
    // TODO: Убрать отладочные логи — конец
  }

  // show_file.php and rest/download.json have no CORS headers — route through /bx24-proxy.
  // In dev: Vite proxy (configured via VITE_BX_PORTAL in vite.config.ts).
  // In prod: nginx proxy (location /bx24-proxy/ { proxy_pass <portal>; }).
  if (url.includes('show_file.php') || url.includes('/rest/download.json')) {
    const parsed = new URL(url);
    url = '/bx24-proxy' + parsed.pathname + parsed.search;
    // TODO: Убрать отладочные логи — начало
    dbg(`fetchDiskFile: проксирую через /bx24-proxy → ${url.substring(0, 80)}...`);
    // TODO: Убрать отладочные логи — конец
  }

  // TODO: Убрать отладочные логи — начало
  dbg(`fetchDiskFile: пытаюсь fetch(${url.substring(0, 120)}...)`);
  // TODO: Убрать отладочные логи — конец

  try {
    const resp = await fetch(url, { credentials: 'include' });
    // TODO: Убрать отладочные логи — начало
    dbg(`fetchDiskFile: ответ ${resp.status} ${resp.statusText}, Content-Type=${resp.headers.get('content-type')}`);
    // TODO: Убрать отладочные логи — конец
    if (!resp.ok) throw new Error(`Ошибка загрузки файла: ${resp.status} ${resp.statusText}`);
    const buf = await resp.arrayBuffer();
    // TODO: Убрать отладочные логи — начало
    dbg(`fetchDiskFile: скачано ${buf.byteLength} байт`);
    // TODO: Убрать отладочные логи — конец
    return buf;
  } catch (e) {
    // TODO: Убрать отладочные логи — начало
    if (e instanceof TypeError && e.message === 'Failed to fetch') {
      dbg(`fetchDiskFile: ОШИБКА CORS/сеть — Failed to fetch. URL=${url.substring(0, 120)}...`);
    } else {
      dbg(`fetchDiskFile: ОШИБКА — ${e instanceof Error ? e.message : String(e)}`);
    }
    // TODO: Убрать отладочные логи — конец
    throw e;
  }
}

/**
 * Download a file from Bitrix24 Disk by its file ID.
 * Calls disk.file.get first to get a proper DOWNLOAD_URL with auth token,
 * then fetches the file content.
 * This is the recommended way to download files — the DOWNLOAD_URL
 * from disk.folder.getchildren may not work directly due to CORS.
 */
export async function fetchDiskFileById(fileId: number): Promise<ArrayBuffer> {
  // TODO: Убрать отладочные логи — начало
  dbg(`fetchDiskFileById: получаю метаданные файла id=${fileId}...`);
  // TODO: Убрать отладочные логи — конец
  const file = await getDiskFile(fileId);
  // TODO: Убрать отладочные логи — начало
  dbg(`fetchDiskFileById: получили DOWNLOAD_URL = ${(file.DOWNLOAD_URL ?? '').substring(0, 120)}...`);
  // TODO: Убрать отладочные логи — конец
  if (!file.DOWNLOAD_URL) throw new Error(`У файла id=${fileId} нет DOWNLOAD_URL`);
  return fetchDiskFile(file.DOWNLOAD_URL);
}
